import { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import styles from './AuthForm.module.scss';

import TextInput from '@/components/new-order/common/TextInput';
import { Button } from '@/components/new-order/common/Button';
import SocialButton from '@/components/new-order/common/SocialButton';
import BackButton from '@/components/new-order/common/BackButton';
import CaptchaDisclaimer from '@/components/affiliate/AuthScreen/CaptchaDisclaimer';

import errorLogger from '@/services/logger/ErrorLogger';
import analyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';

interface EmailSignUpFormProps {
  selectedPlanSku: string;
  sendOTCRequest: (emailAdress: string) => Promise<void>;
  onSubmit: (email: string) => void;
  onBackButtonClick: () => void;
  presetEmail?: string;
}

const EmailSignUpForm = ({
  selectedPlanSku,
  sendOTCRequest,
  onSubmit,
  onBackButtonClick,
  presetEmail,
}: EmailSignUpFormProps) => {
  const intl = useIntl();

  const [email, setEmail] = useState(presetEmail || '');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onEmailChange = (event: { value: string; isValid: boolean }) => {
    setEmail(event.value);
    setIsValid(event.isValid);
    setError('');
  };

  const validateEmail = () => {
    if (email.length === 0) {
      setError(
        intl.formatMessage({
          id: 'authScreen.emailSignup.errors.noEmail',
          defaultMessage: 'Email is required',
          description: 'Error shown when submitting email form w/o email',
        }),
      );

      return false;
    }
    if (!isValid) {
      setError(
        intl.formatMessage({
          id: 'authScreen.emailSignup.errors.invalidEmail',
          defaultMessage: 'Email is invalid',
          description: 'Error shown when submitting email of invalid format',
        }),
      );

      return false;
    }

    return true;
  };

  const handleSubmit: React.SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    if (validateEmail()) {
      setIsSubmitting(true);
      sendOTCRequest(email).then(
        () => {
          analyticsEventHandler.logOTCEmailSubmit();
          onSubmit(email);
          setIsSubmitting(false);
        },
        (errorResponse: unknown) => {
          errorLogger.logError(errorResponse as Error);
          setIsSubmitting(false);
          setError(
            intl.formatMessage({
              id: 'authScreen.emailSignup.errors.serverError',
              defaultMessage: 'Something went wrong. Please try again later.',
              description: 'Error shown when server returns an error',
            }),
          );
        },
      );
    }
  };

  return (
    <div className={styles.wrapper}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <BackButton onClick={onBackButtonClick} />
        <h3 className={styles.title}>
          <FormattedMessage
            id="authScreen.emailSignup.title"
            defaultMessage="Sweet, let’s get you started"
            description="Title of email form at the start of the sign up flow"
          />
        </h3>
        <TextInput
          value={email}
          handleChange={onEmailChange}
          error={error}
          type="email"
          autoComplete="email"
          disabled={isSubmitting}
          placeholder={intl.formatMessage({
            id: 'authScreen.emailSignUp.emailInputPlaceholder',
            defaultMessage: 'Enter your email',
            description: 'Placeholder of the email input field',
          })}
          onBlur={validateEmail}
        />

        <Button type="submit" disabled={isSubmitting}>
          <FormattedMessage
            id="authScreen.emailSignup.submitButton"
            defaultMessage="Continue"
            description="Email sign up form submit button text"
          />
        </Button>

        <span className={styles.socialButtonsLabel}>
          <FormattedMessage
            id="authScreen.emailSignup.useSocialsLabel"
            defaultMessage="or sign in with"
            description="Label between email sign up form and section with social logins"
          />
        </span>

        <div className={styles.socialButtonsWrapper}>
          <SocialButton type="google" selectedPlanSku={selectedPlanSku} />
          <SocialButton type="apple" selectedPlanSku={selectedPlanSku} />
          <SocialButton type="setapp" selectedPlanSku={selectedPlanSku} />
        </div>

        <CaptchaDisclaimer />
      </form>
    </div>
  );
};

export default EmailSignUpForm;
